import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { ROLE_LABEL } from '@/lib/auth'
import { useAuth } from '@/lib/auth/use-auth'

function initialsOf(fullName: string): string {
  const parts = fullName.trim().split(/\s+/)
  if (parts.length === 0) return '?'
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
  return (first + last).toUpperCase()
}

export function UserMenu() {
  const { session, signOut } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [signingOut, setSigningOut] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return

    function onPointerDown(event: PointerEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  if (!session) return null
  const { user } = session

  async function handleSignOut() {
    setSigningOut(true)
    try {
      await signOut('user')
      navigate('/login', { replace: true })
    } finally {
      setSigningOut(false)
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2.5 rounded-[100px] py-1.5 pl-1.5 pr-3.5
                   transition-colors hover:bg-surface-strong active:bg-hairline"
      >
        <span
          className="t-caption-strong grid h-8 w-8 place-items-center rounded-full
                     bg-surface-strong text-ink"
          aria-hidden="true"
        >
          {initialsOf(user.fullName)}
        </span>
        <span className="t-nav-link hidden text-ink sm:block">{user.fullName}</span>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path
            d="m3 4.5 3 3 3-3"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-72 rounded-[16px] border border-hairline
                     bg-canvas p-2 shadow-[0_4px_12px_rgba(0,0,0,0.04)]"
        >
          <div className="border-b border-hairline-soft px-3 py-3">
            <p className="t-title-sm truncate text-ink">{user.fullName}</p>
            <p className="t-caption mt-0.5 truncate text-muted">{user.email}</p>
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              <span className="t-caption-strong rounded-[100px] bg-surface-strong px-2.5 py-1 text-ink">
                {ROLE_LABEL[user.role]}
              </span>
              {user.shift && <span className="t-caption text-muted">{user.shift}</span>}
            </div>
          </div>

          <MenuItem
            onClick={() => {
              setOpen(false)
              navigate('/change-password')
            }}
          >
            Change password
          </MenuItem>

          <MenuItem onClick={handleSignOut} disabled={signingOut} tone="danger">
            Sign out
          </MenuItem>
        </div>
      )}
    </div>
  )
}

function MenuItem({
  children,
  onClick,
  disabled,
  tone = 'default',
}: {
  children: ReactNode
  onClick: () => void
  disabled?: boolean
  tone?: 'default' | 'danger'
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      className={`t-body-sm w-full rounded-[10px] px-3 py-2.5 text-left transition-colors
                  hover:bg-surface-soft active:bg-surface-strong
                  disabled:cursor-not-allowed disabled:opacity-50
                  ${tone === 'danger' ? 'text-semantic-down' : 'text-ink'}`}
    >
      {children}
    </button>
  )
}
